import { keepPreviousData } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { useGetETFExploreTable } from '#/api/generated/explore-table/explore-table';
import { DataTable } from '#/components/data/DataTable';
import { DataTableSkeleton } from '#/components/data/DataTableSkeleton';
import { DEFAULT_SORTING } from '#/components/index/ETFsExploreTable/constants';
import {
  etfRowId,
  etfsColumns,
  getApiSortKey,
  getSortColumn,
  getSortDirection,
} from '#/components/index/ETFsExploreTable/data';
import { getSearchSortParams } from '#/components/search/searchPagination';
import { Icon } from '#/components/ui/icons';
import { QueryBoundary } from '#/components/ui/QueryBoundary';
import type { ReturnsTimeframeId } from '#/components/ui/returnsTimeframe';
import {
  getReturnsTimeframeFromSorting,
  syncSortingToReturnsTimeframe,
} from '#/components/ui/returnsTimeframe';
import { ReturnsTimeframeFilter } from '#/components/ui/ReturnsTimeframeFilter';
import { Spinner } from '#/components/ui/Spinner';
import type { SectorSlug } from '#/types';
import { cn } from '#/utils/cn';

const ETF_RETURN_COLUMN_IDS = {
  oneYear: 'trailing_1yr_return',
  fiveYear: 'return_5Y_annualized',
} as const;

export function ETFsExploreTable({
  category,
  children,
  limit,
  title = 'Exchange-Traded Funds',
  id = 'explore-etfs',
  initialSorting = DEFAULT_SORTING,
  tickerTrack,
}: {
  category?: SectorSlug;
  children?: React.ReactNode;
  limit?: number;
  title?: string;
  id?: string;
  initialSorting?: SortingState;
  /**
   * Overrides the ticker link's analytics annotation. Omitted keeps the
   * factory default (`explore_table_ticker` / `etfs_explore`) so the home page
   * is unchanged; the category index passes `explore_table` context.
   */
  tickerTrack?: { track: string; context: string };
}) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [returnsTimeframe, setReturnsTimeframe] = useState<ReturnsTimeframeId>(
    () => getReturnsTimeframeFromSorting(sorting, ETF_RETURN_COLUMN_IDS),
  );
  const activeSortColumn = getSortColumn(sorting, 'expense_ratio');
  const searchSortParams = getSearchSortParams('etf', sorting);
  const query = useGetETFExploreTable(
    {
      limit,
      order_by: getSortDirection(sorting),
      sector_slug: category,
      sort_by: getApiSortKey(activeSortColumn),
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const columns = useMemo(
    () => getEtfColumnsForTimeframe(returnsTimeframe, tickerTrack),
    [returnsTimeframe, tickerTrack],
  );

  function handleReturnsTimeframeChange(nextTimeframe: ReturnsTimeframeId) {
    setReturnsTimeframe(nextTimeframe);
    setSorting((currentSorting) =>
      syncSortingToReturnsTimeframe(
        currentSorting,
        nextTimeframe,
        ETF_RETURN_COLUMN_IDS,
      ),
    );
  }

  return (
    <section
      id={id}
      className="flow flow-inner-8 min-w-0 scroll-mt-(--scroll-offset)"
    >
      {children}

      <div className="flow-24 p-gap border-divider-subtle -mx-gap min-h-full border-b sm:mx-0 sm:rounded sm:border">
        <QueryBoundary
          query={query}
          loading={
            <DataTableSkeleton
              columns={columns.length}
              rows={limit ?? 10}
              title={title}
              label={`Loading ${title.toLowerCase()}…`}
            />
          }
        >
          {(response) => {
            const rows = response.data.results ?? [];

            return (
              <>
                <div className="mb-16 flex flex-col items-start gap-8 sm:flex-row sm:flex-wrap sm:justify-between sm:gap-x-12">
                  <h3 className="text-fluid-base text-fg-header min-w-0 flex-1 font-semibold">
                    <span id={`${id}-title`} className="text-balance">
                      {title}
                    </span>
                    <span
                      aria-hidden={!query.isFetching}
                      className={cn(
                        'text-fg-muted pointer-events-none ml-8 inline-flex align-middle transition-opacity duration-150',
                        query.isFetching
                          ? 'opacity-100 delay-20'
                          : 'invisible opacity-0 delay-0',
                      )}
                    >
                      <Spinner
                        label={`Refreshing ${title.toLowerCase()}…`}
                        className="w-auto py-0"
                        size={16}
                      />
                    </span>
                  </h3>

                  <div className="w-full sm:w-auto">
                    <Link
                      to="/search"
                      search={{
                        query: '',
                        investment_type: 'etf',
                        sort_by: searchSortParams.sort_by,
                        order_by: searchSortParams.order_by,
                        equity_sector_slug: undefined,
                        equity_industry_slug: undefined,
                        market_cap_categories: undefined,
                        fund_category_slug: category ? [category] : undefined,
                        equity_next_pagination_token: undefined,
                        equity_prev_pagination_token: undefined,
                        etf_next_pagination_token: undefined,
                        etf_prev_pagination_token: undefined,
                        mutual_fund_next_pagination_token: undefined,
                        mutual_fund_prev_pagination_token: undefined,
                        listing_type: undefined,
                        exchange: undefined,
                      }}
                      id={`${id}-view-more`}
                      aria-labelledby={`${id}-view-more ${id}-title`}
                      className="mdc-btn mdc-btn--md mdc-btn--link -my-10 self-start"
                    >
                      Filter and View More
                      <Icon
                        id="arrow-right"
                        size={16}
                        className="ml-4 inline-block"
                      />
                    </Link>
                  </div>
                </div>

                <div className="flex justify-end">
                  <ReturnsTimeframeFilter
                    timeframe={returnsTimeframe}
                    onChange={handleReturnsTimeframeChange}
                    trackContext="etfs_explore"
                  />
                </div>

                <div className="relative mt-16">
                  <DataTable
                    aria-label={`${title} table`}
                    className={cn(
                      '-mx-gap transition-opacity sm:mx-0',
                      query.isFetching && 'opacity-60',
                    )}
                    columns={columns}
                    data={rows}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    manualSorting
                    getRowId={etfRowId}
                    pageSize={10}
                    emptyState="No ETFs on file"
                    trackContext="etfs_explore"
                  />
                  <div
                    aria-hidden={!query.isFetching}
                    className={cn(
                      'pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity',
                      query.isFetching ? 'opacity-100' : 'invisible opacity-0',
                    )}
                  >
                    <Spinner
                      label={`Refreshing ${title.toLowerCase()} table…`}
                    />
                  </div>
                </div>
              </>
            );
          }}
        </QueryBoundary>
      </div>
    </section>
  );
}

function getEtfColumnsForTimeframe(
  returnsTimeframe: ReturnsTimeframeId,
  tickerTrack?: { track: string; context: string },
) {
  const columnsById = new Map<string, ReturnType<typeof etfsColumns>[number]>(
    etfsColumns('ETF', { tickerTrack }).map((column) => [
      column.id ?? '',
      column,
    ]),
  );

  return [
    columnsById.get('ticker'),
    columnsById.get('expense_ratio'),
    columnsById.get('price'),
    columnsById.get(
      returnsTimeframe === '1Y'
        ? ETF_RETURN_COLUMN_IDS.oneYear
        : ETF_RETURN_COLUMN_IDS.fiveYear,
    ),
  ].filter(
    (column): column is ReturnType<typeof etfsColumns>[number] =>
      column != null,
  );
}
